import { useLDClient } from 'launchdarkly-react-client-sdk';
import { LDUser } from 'launchdarkly-js-sdk-common';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';

import { getUserInformation } from 'src/modules/user/selectors/userSelectors';

import { languageNameToI18nLanguage } from 'src/config/i18n.config';
import { SharedPreferencesKeys, UserState } from 'src/modules/user/models/user';
import { DefaultSupportedLocaleKey } from 'src/shared/models/shared';

export const stateUserToLDUser = (user: UserState): LDUser => {
    // copied from `get_launch_darkly_user` method in django_backend/feature_flags.py
    // append custom attribute 'state' for use in targeting rules
    const fakeDomain = user.company.replace(/\W+/g, '').toLowerCase();

    const canBeBucketedForDPStreaks =
        user.sharedPreferences.find(
            preference => preference.key === SharedPreferencesKeys.CanBeBucketedForDPStreaks,
        )?.value === 'true';

    const ldUser: LDUser = {
        key: user.uuid,
        email: `${user.uuid}@${fakeDomain}.com`,
        country: user.country ?? '',

        custom: {
            state: user.state ?? '',
            currentLocale:
                (user.languagePreferences.length > 0 &&
                    languageNameToI18nLanguage[user.languagePreferences[0]]) ??
                DefaultSupportedLocaleKey,
            canBeBucketedForDPStreaks,
        },
    };

    return ldUser;
};

export const FeatureFlagsManager: React.FC = () => {
    const ldClient = useLDClient();
    const user = useSelector(getUserInformation);

    useEffect(() => {
        if (!ldClient) return;

        const currentLdUser = ldClient.getUser();

        // handles logged user
        if (!!user.uuid) {
            const ldUser = stateUserToLDUser(user);

            ldClient.identify(ldUser);
        }

        // handles logout
        if (currentLdUser && !currentLdUser?.anonymous && !user.uuid) {
            ldClient.identify({ anonymous: true });
        }
    }, [user, ldClient]);

    return null;
};
